import { useCallback, useEffect, useState, Fragment } from 'react';
import { toast } from 'sonner';
import { Banknote, Loader2, RefreshCw } from 'lucide-react';

import api from '../lib/api';
import { fmtCurrency } from '../lib/localeUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { skeletonKeys } from '../lib/listKeys';
import { VarianceBadge, HandoverRowDetail } from '../components/shift/HandoverRow';
import { ShiftHandoverDialog } from '../components/shift/ShiftHandoverDialog';

export default function ShiftHandoversPage() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState('own');
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(null);
  const [counting, setCounting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/shift/handovers', { params: { date_from: from, date_to: to, limit: 100 } });
      setRows(data.handovers || []);
      setScope(data.scope);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load handovers');
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="shift-handovers-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Banknote className="h-5 w-5 text-primary" /> Shift Handovers
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Every closed shift with what the till took and what was actually counted in the
          drawer. {scope === 'own' ? 'You are seeing your own handovers.' : 'Branch-wide view.'}
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-end justify-between gap-3 flex-wrap">
          <div className="flex gap-3 flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="handover-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="handover-to" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setCounting(true)} className="gap-2" data-testid="count-drawer-btn">
              <Banknote className="h-4 w-4" /> Count drawer &amp; close shift
            </Button>
            <Button variant="outline" onClick={load} className="gap-2" data-testid="handovers-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Closed</TableHead>
                <TableHead>Cashier</TableHead>
                <TableHead>Till</TableHead>
                <TableHead className="text-right">Sales</TableHead>
                <TableHead className="text-right">Expected cash</TableHead>
                <TableHead className="text-right">Counted</TableHead>
                <TableHead>Variance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? skeletonKeys(4, 'handover-skeleton').map((key) => (
                <TableRow key={key}><TableCell colSpan={7}><div className="h-9 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              )) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="handovers-empty">
                    <Banknote className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p>No shift handovers recorded yet.</p>
                    <p className="text-xs mt-1">Cashiers create one by counting the drawer when they lock the till.</p>
                  </TableCell>
                </TableRow>
              ) : rows.map((h) => (
                <Fragment key={h.handover_id}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => setOpen(open === h.handover_id ? null : h.handover_id)}
                    data-testid={`handover-row-${h.handover_id}`}
                  >
                    <TableCell className="text-sm whitespace-nowrap">{new Date(h.ended_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{h.user_name}</TableCell>
                    <TableCell className="text-sm">{h.terminal_label || 'Browser session'}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {fmtCurrency(h.gross_sales)}
                      <span className="text-xs text-muted-foreground block">{h.sales_count} sale{h.sales_count === 1 ? '' : 's'}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtCurrency(h.expected_cash)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{fmtCurrency(h.counted_total)}</TableCell>
                    <TableCell><VarianceBadge handover={h} /></TableCell>
                  </TableRow>
                  {open === h.handover_id && (
                    <TableRow key={`${h.handover_id}-detail`}>
                      <TableCell colSpan={7} className="bg-muted/30">
                        <HandoverRowDetail handover={h} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ShiftHandoverDialog
        open={counting} lockMode={false}
        onClose={() => setCounting(false)}
        onDone={() => { setCounting(false); load(); }}
      />
    </div>
  );
}
